import { toast } from 'sonner';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Download } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import { Button } from '@/shared/components/ui/button';
import {
  downloadAdvisorReportPdf,
  getAdvisorReport,
  type V2AdvisorReport,
} from './api/v2AdvisorReportApi';
import { V2AdvisorReportView } from './components/V2AdvisorReportView';

/**
 * The advisor's planning view of an MVP 2 result (Feature 110).
 *
 * Its own route rather than a tab on the client report: only the advisor who
 * sent the assessment may open it, and only once the client shared. A client
 * who has not shared produces a 403 rather than a 404, and the difference is
 * worth showing — "not shared yet" is something the advisor can follow up on,
 * where "not found" would suggest the assessment is gone.
 */
export function AdvisorReportPage() {
  const { id = '' } = useParams();
  const {
    data: report,
    isLoading,
    error,
  } = useQuery<V2AdvisorReport>({
    queryKey: ['longevity-assessment', 'advisor-report', id],
    queryFn: () => getAdvisorReport(id),
    enabled: !!id,
    retry: false,
  });

  // The api client's interceptor rejects with a normalised error — message,
  // statusCode, code — and no `response`. Reading `response.status` here left
  // the shared/not-shared distinction dead: every failure fell through to the
  // generic message.
  const status = (error as { statusCode?: number } | null)?.statusCode;

  const [downloading, setDownloading] = useState(false);
  const handleDownload = async () => {
    setDownloading(true);
    try {
      await downloadAdvisorReportPdf(id);
    } catch (error) {
      // Non-fatal for the page — sharing can be revoked between opening the
      // report and asking for the file — but not silent. The button returned to
      // its resting state with no file and no explanation, so the advisor had no
      // way to tell a failure from a slow browser.
      console.error('Failed to download the advisor report PDF:', error);
      toast.error('Could not prepare the PDF. The report on this page is unaffected.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-4xl px-4 py-8">
        <div className="mb-6 flex items-center justify-between">
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden /> Back to dashboard
          </Link>
          {/* Only once the report loaded: the same gate that served it is the
              one the download goes through, so offering it before would be
              offering a 404. */}
          {report && (
            <Button
              variant="outline"
              size="sm"
              className="gap-2"
              disabled={downloading}
              onClick={handleDownload}
            >
              <Download className="h-4 w-4" aria-hidden />
              {downloading ? 'Preparing…' : 'Download PDF'}
            </Button>
          )}
        </div>

        {isLoading && <p className="text-center text-gray-500">Loading report…</p>}

        {status === 403 && (
          <div className="rounded-2xl border border-gray-200 bg-white p-8 text-center">
            <h1 className="font-serif text-2xl text-gray-900">Results not shared yet</h1>
            <p className="mx-auto mt-3 max-w-md text-[15px] leading-relaxed text-gray-600">
              Your client completed their assessment but chose to keep the results private. They can
              share them from their dashboard at any time.
            </p>
          </div>
        )}

        {error && status !== 403 && (
          <div className="rounded-2xl border border-gray-200 bg-white p-8 text-center">
            <h1 className="font-serif text-2xl text-gray-900">Report not available</h1>
            <p className="mx-auto mt-3 max-w-md text-[15px] leading-relaxed text-gray-600">
              This assessment has no advisor report. Only completed MVP 2 assessments you sent have
              one.
            </p>
          </div>
        )}

        {report && <V2AdvisorReportView report={report} />}
      </div>
    </div>
  );
}
